import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from '../utils/axiosConfig';
import { formatCurrency } from '../utils/currency';
import { formatDate, formatDateTime } from '../utils/dates';
import { lineGstBreakdown } from '../utils/gst';
import { t } from '../utils/translations';
import './Products.css';
import './Returns.css';

const round3 = (n) => Math.round((parseFloat(n) || 0) * 1000) / 1000;

const Returns = () => {
  const [searchParams] = useSearchParams();
  const [returns, setReturns] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState('');
  const [returnableItems, setReturnableItems] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [gstEnabled, setGstEnabled] = useState(false);
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [qtyMap, setQtyMap] = useState({});

  useEffect(() => {
    fetchData().then(() => {
      const invoiceFromQuery = searchParams.get('invoice');
      if (invoiceFromQuery) {
        setShowModal(true);
        loadInvoiceItems(invoiceFromQuery);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [returnsRes, invoicesRes] = await Promise.all([
        axios.get('/api/returns'),
        axios.get('/api/invoices')
      ]);
      setReturns(returnsRes.data.returns || []);
      setInvoices(invoicesRes.data.invoices || []);
    } catch (error) {
      console.error(error);
      alert(error.response?.data?.message || 'Failed to load returns');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setSelectedInvoiceId('');
    setSelectedInvoice(null);
    setReturnableItems([]);
    setQtyMap({});
    setReason('');
    setNotes('');
    setGstEnabled(false);
    setShowModal(true);
  };

  const loadInvoiceItems = async (invoiceId) => {
    setSelectedInvoiceId(invoiceId);
    setReturnableItems([]);
    setQtyMap({});
    setSelectedInvoice(null);
    if (!invoiceId) return;

    try {
      const res = await axios.get(`/api/returns/invoice/${invoiceId}/items`);
      setSelectedInvoice(res.data.invoice || null);
      setGstEnabled(Boolean(res.data.gst_enabled));
      const items = res.data.items || [];
      setReturnableItems(items);
      const initial = {};
      items.forEach((item) => {
        initial[item.id] = 0;
      });
      setQtyMap(initial);
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to load invoice items');
    }
  };

  const lineRefund = (item, qty) => {
    const returnQty = parseFloat(qty) || 0;
    const remaining = parseFloat(item.remaining_qty) || 0;
    if (returnQty <= 0 || remaining <= 0) return 0;
    const origDisc = parseFloat(item.discount) || 0;
    const lineDisc = round3(origDisc * (returnQty / remaining));
    const rate = gstEnabled ? (parseFloat(item.gst_rate) || 0) : 0;
    const inclusive = gstEnabled ? Boolean(item.gst_inclusive) : false;
    return round3(lineGstBreakdown(returnQty, item.unit_price, lineDisc, rate, inclusive).lineTotal);
  };

  const refundPreview = useMemo(() => {
    return round3(
      returnableItems.reduce((sum, item) => sum + lineRefund(item, qtyMap[item.id]), 0)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [returnableItems, qtyMap, gstEnabled]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedInvoiceId) {
      alert('Select an invoice');
      return;
    }

    const items = returnableItems
      .map((item) => ({
        invoice_item_id: item.id,
        quantity: parseFloat(qtyMap[item.id]) || 0
      }))
      .filter((item) => item.quantity > 0);

    if (!items.length) {
      alert('Enter quantity for at least one product to return');
      return;
    }

    setSaving(true);
    try {
      const res = await axios.post('/api/returns', {
        invoice_id: selectedInvoiceId,
        reason,
        notes,
        items
      });
      const refund = res.data.refund_amount ?? refundPreview;
      const newTotal = res.data.invoice?.total_amount;
      setShowModal(false);
      await fetchData();
      const totalMsg = newTotal != null
        ? ` Invoice total is now ${formatCurrency(newTotal)}.`
        : '';
      alert(`Return processed. Refund ${formatCurrency(refund)}. Stock restored.${totalMsg}`);
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to process return');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="page-container"><div className="container">{t('Loading...')}</div></div>;
  }

  return (
    <div className="page-container">
      <div className="container">
        <div className="page-header">
          <h1>Product Returns</h1>
          <button type="button" className="btn btn-primary" onClick={openCreate}>
            New Return
          </button>
        </div>

        <div className="card returns-help">
          <p>
            Select a sold invoice, choose products and quantities to return.
            Stock is restored and the invoice amount is reduced automatically
            (dashboard sales and invoice totals update too).
          </p>
        </div>

        <div className="card">
          <table className="table">
            <thead>
              <tr>
                <th>Return #</th>
                <th>Invoice #</th>
                <th>Customer</th>
                <th>Items</th>
                <th>Refund</th>
                <th>Reason</th>
                <th>Date</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {returns.length === 0 ? (
                <tr>
                  <td colSpan="8" style={{ textAlign: 'center' }}>No returns yet</td>
                </tr>
              ) : (
                returns.map((ret) => (
                  <tr key={ret.id}>
                    <td>{ret.return_number}</td>
                    <td>{ret.invoice_number || '-'}</td>
                    <td>{ret.customer_name || '-'}</td>
                    <td>
                      {(ret.items || []).map((item) => (
                        <div key={item.id} className="return-item-line">
                          {item.description} × {item.quantity}
                          {item.line_total != null ? ` (${formatCurrency(item.line_total)})` : ''}
                        </div>
                      ))}
                    </td>
                    <td>{formatCurrency(ret.refund_amount || 0)}</td>
                    <td>{ret.reason || '-'}</td>
                    <td>{formatDateTime(ret.created_at)}</td>
                    <td>
                      <span className={`status-badge status-${ret.status || 'completed'}`}>
                        {ret.status || 'completed'}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {showModal && (
          <div className="modal">
            <div className="modal-content returns-modal">
              <div className="modal-header">
                <h2>Process Product Return</h2>
                <button type="button" className="close-btn" onClick={() => setShowModal(false)}>×</button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>Invoice *</label>
                  <select
                    value={selectedInvoiceId}
                    onChange={(e) => loadInvoiceItems(e.target.value)}
                    required
                  >
                    <option value="">Select invoice</option>
                    {invoices.map((inv) => (
                      <option key={inv.id} value={inv.id}>
                        {inv.invoice_number} — {inv.customer_name || 'Customer'} — {formatCurrency(inv.total_amount || 0)}
                      </option>
                    ))}
                  </select>
                </div>

                {selectedInvoice && (
                  <div className="return-invoice-meta">
                    Current invoice total: <strong>{formatCurrency(selectedInvoice.total_amount || 0)}</strong>
                    {' · '}
                    Date: {formatDate(selectedInvoice.issue_date)}
                  </div>
                )}

                {selectedInvoiceId && (
                  <div className="form-group">
                    <label>Products to return</label>
                    {returnableItems.length === 0 ? (
                      <div className="return-empty">No returnable products left on this invoice.</div>
                    ) : (
                      <div className="return-items-list">
                        {returnableItems.map((item) => {
                          const qty = parseFloat(qtyMap[item.id]) || 0;
                          const itemRefund = lineRefund(item, qty);
                          return (
                            <div key={item.id} className="return-item-row">
                              <div>
                                <strong>{item.description}</strong>
                                <div className="return-item-sub">
                                  Sold: {item.sold_qty ?? item.quantity} | Already returned: {item.returned_qty} | Remaining: {item.remaining_qty}
                                </div>
                                <div className="return-item-sub">
                                  {formatCurrency(item.unit_price || 0)} each
                                  {qty > 0 ? ` · Line refund: ${formatCurrency(itemRefund)}` : ''}
                                </div>
                              </div>
                              <div className="return-qty-wrap">
                                <label>Qty</label>
                                <input
                                  type="number"
                                  min="0"
                                  max={item.remaining_qty}
                                  step="1"
                                  value={qtyMap[item.id] ?? 0}
                                  onChange={(e) =>
                                    setQtyMap((prev) => ({
                                      ...prev,
                                      [item.id]: e.target.value
                                    }))
                                  }
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {refundPreview > 0 && (
                  <div className="return-refund-summary">
                    <span>Refund amount</span>
                    <strong>{formatCurrency(refundPreview)}</strong>
                    {selectedInvoice && (
                      <div className="return-item-sub">
                        New invoice total (approx):{' '}
                        {formatCurrency(Math.max(0, (parseFloat(selectedInvoice.total_amount) || 0) - refundPreview))}
                      </div>
                    )}
                  </div>
                )}

                <div className="form-group">
                  <label>Reason</label>
                  <select value={reason} onChange={(e) => setReason(e.target.value)}>
                    <option value="">Select reason</option>
                    <option value="Damaged">Damaged</option>
                    <option value="Wrong item">Wrong item</option>
                    <option value="Customer request">Customer request</option>
                    <option value="Expired">Expired</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Notes</label>
                  <textarea
                    rows="3"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Optional notes"
                  />
                </div>

                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                  <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={saving}>
                    {saving ? 'Processing...' : 'Process Return'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Returns;
